import { Box, Flex, Spinner } from '@chakra-ui/react';
import {
  forwardRef,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { FormBuilder } from '@/components/formBuilder';
import { KText } from '@/ui-library/components';
import { ApprovalFlowSteps } from '@/components/ats/ApprovalFlowSteps/ApprovalFlowSteps';
import { useShowToast } from '@/hooks';
import {
  useGetApprovalsByEntityQuery,
  // useValidatePreapprovalMutation,
} from '../api';
import type {
  OfferApprovalFormValues,
  OfferApprovalRef,
  OfferApprovalProps,
} from './offer-approvals/OfferApproval.interface';
import { ScorecardTableExtension } from './offer-approvals/ScorecardTableExtension';
import { AttachFilesPicker } from './offer-approvals/AttachFilesPicker';
import { SelectedAttachmentChips } from './offer-approvals/SelectedAttachmentChips';
import {
  attachmentKey,
  useOfferAttachments,
} from './offer-approvals/useOfferAttachments';
import { type SelectionRef } from './offer-approvals/attachFilesApi';
import { approvalAttachmentErrorCopy } from './offer-approvals/attachFilesCopy';
import { useOfferStore } from '../store';

export const OfferApproval = forwardRef<OfferApprovalRef, OfferApprovalProps>(
  (props, comRef) => {
    const offerId = useOfferStore((state) => state.offerId);
    const {
      entityId = offerId,
      entityType = 'offer',
      currentOffer = {},
    } = props;
    const formRef = useRef<any>(null);

    const { data: approvalData, isLoading: isApprovalLoading } =
      useGetApprovalsByEntityQuery({
        entityId,
        entityType,
      });

    // Grouped attachment picker. Only offer documents are pre-checked (amendment_12);
    // resume and scorecards are an explicit choice. Selection is a set of references,
    // persisted via seam_2 on "Add" / chip removal — not bundled into the note payload
    // below.
    const attachments = useOfferAttachments(offerId);
    const toast = useShowToast();

    // The picker's "Add" path already toasts on a seam_2 failure
    // (AttachFilesPicker's handleAdd) — the chip ✕ reaches the exact same
    // mutation via removeConfirmed and was previously wired to it directly,
    // leaving a rejected persist an unhandled promise with no user-visible
    // feedback at all.
    // Which chip is mid-removal. The ✕ PUTs the whole remaining selection and
    // the chip only disappears once the server confirms, so without this the
    // chip just sits there looking untouched for the length of the request.
    // Keyed rather than a boolean: only the chip being removed should show a
    // loader, not every chip on the row.
    const [removingKey, setRemovingKey] = useState<string | null>(null);

    const handleRemoveAttachment = async (attachment: SelectionRef) => {
      setRemovingKey(attachmentKey(attachment));
      try {
        await attachments.removeConfirmed(attachment);
      } catch (error) {
        toast({
          title: 'Failed to remove attachment',
          description: approvalAttachmentErrorCopy(error),
          status: 'error',
        });
      } finally {
        // Cleared on failure too — the chip stays, so leaving it spinning would
        // read as a removal still in progress that will never land.
        setRemovingKey(null);
      }
    };

    useImperativeHandle(comRef, () => ({
      getModel: async () => {
        const formValues = formRef.current?.values as OfferApprovalFormValues;
        const errors = await formRef.current?.validateForm();

        const hasValidData = !errors || Object.keys(errors).length === 0;

        // The kula editor writes HTML straight to the Formik field (debounced,
        // flushed on blur by KulaFormEditor), so the field IS the payload —
        // unlike the legacy editor, which exposed a tiptap instance and left
        // the caller to pull getHTML() off it. There is no instance to read
        // here, and reaching for one would return an empty note.
        return {
          hasValidData,
          payload: {
            note: formValues?.note || '',
          },
        };
      },
    }));

    const initialValues: OfferApprovalFormValues = useMemo(
      () => ({
        note: currentOffer?.approval_request_note || '',
      }),
      [currentOffer]
    );

    // CORE-5264 Decision 3: the manual "+ Scorecard history" insert button is
    // gone — the "All previous scorecards" attachment replaces it (keeping both
    // would show the approver the same scorecards twice).
    // ScorecardTableExtension itself STAYS registered below: its parseHTML
    // still renders scorecard tables saved in approval_request_note on offers
    // already in flight (G1/G3). Only the insertion path is removed.
    const formConfig = useMemo(
      () => [
        {
          name: 'note',
          // `editor2` is the kula-ai Editor (KulaFormEditor); `editor` is the
          // legacy in-repo tiptap one. The toolbar slot below only exists on
          // the kula Editor, which is what moves "Attach files" off the card
          // header and into the toolbar row.
          type: 'editor2',
          isRequired: false,
          placeholder: 'Add a note for the approvers...',
          // FormBuilder gives every field `margin: 1em 0` — 16px above and
          // below. Inside this card that reads as a gap under the header rule
          // and another above the chips, on top of the editor's own 12px
          // padding. Zeroed for this field only; the shared default stays as
          // it is for every other form.
          containerStyles: { margin: 0 },
          editorStyles: {
            height: '150px',
          },
          editorProps: {
            // The card already draws the border and the header above it, so
            // the editor's own chrome would read as a second box inside it.
            //
            // The root ships `p-3 pr-1` — asymmetric, leaving the toolbar
            // closer to the right edge than the left — so the right is squared
            // to 12px and the box reads uniform on all four sides.
            //
            // `overflow-visible` is what lets that padding be tuned at all: the
            // root clips its own overflow, and the attach-files count badge
            // overhangs its label by 5px, so any smaller top pad sliced the top
            // off the circle. The card around it still clips, and the content
            // area keeps its own scroll container, so nothing else leaks.
            className: '!border-0 !rounded-none !pr-3 !overflow-visible',
            // No "..." overflow: everything it held is reachable in the row
            // itself. Strikethrough and inline code join bold and italic in the
            // marks menu (the editor only ever renders those five together, as
            // one control), and link gets its own button — so dropping the
            // overflow costs nothing rather than hiding three tools behind it.
            // Lists, alignment, text-style and clear-format stay off: Figma
            // 2498:1313 draws none of them.
            toolbarFeatures: {
              bold: true,
              italic: true,
              strike: true,
              code: true,
              link: true,
              image: true,
              overflow: false,
              textStyle: false,
              clearFormat: false,
              list: false,
              align: false,
            },
            // G1/G3: still registered though the insertion button is gone —
            // its parseHTML renders scorecard tables saved in
            // approval_request_note on offers already in flight.
            extraExtensions: [ScorecardTableExtension],
            toolbarEndSlot: (
              <AttachFilesPicker offerId={offerId} attachments={attachments} />
            ),
          },
        },
      ],
      [offerId, attachments]
    );

    const renderApproverNote = () => {
      return (
        <Box
          border="1px solid"
          borderColor="border-color"
          borderRadius="md"
          overflow="hidden"
        >
          <KText
            p="16px 20px"
            borderBottom="1px solid"
            borderColor="border-color"
            bg="secondary-25"
            size="sm-medium"
            color="secondary-900"
          >
            Note to approvers
          </KText>
          <FormBuilder
            formRef={formRef}
            config={formConfig}
            initialValues={initialValues}
          />
          <SelectedAttachmentChips
            attachments={attachments.selectedAttachments}
            onRemove={handleRemoveAttachment}
            uploadingFile={attachments.uploadingFile}
            removingKey={removingKey}
          />
        </Box>
      );
    };

    const renderApproverFlow = () => {
      return (
        <Box border="1px solid" borderColor="border-color" borderRadius="md">
          <KText
            p="16px 20px"
            borderBottom="1px solid"
            borderColor="border-color"
            bg="secondary-25"
            size="sm-medium"
            color="secondary-900"
          >
            Offer approval flow
          </KText>
          <ApprovalFlowSteps
            config={approvalData || {}}
            isLoading={isApprovalLoading}
            onClose={() => {}}
            entityType={entityType}
            entityId={entityId}
            onSuccess={() => {}}
            renderModalInParent={false}
            viewMode={true}
            offerId={offerId}
            handleModalOpen={undefined}
            handleUserSelect={undefined}
          />
        </Box>
      );
    };

    if (isApprovalLoading) {
      return (
        <Flex h="100%" w="100%" justifyContent="center" alignItems="center">
          <Spinner color="primary-600" />
        </Flex>
      );
    }

    return (
      <Flex gap="12px" flexDir="column">
        {renderApproverNote()}
        {renderApproverFlow()}
      </Flex>
    );
  }
);

OfferApproval.displayName = 'OfferApproval';
